import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth";
import { I18nProvider, useI18n } from "./i18n";
import { syncPending } from "./offline";
import BottomTabs from "./components/BottomTabs";
import TermsGate from "./components/TermsGate";
import { C } from "./theme";

import Login from "./pages/Login";
import Register from "./pages/Register";
import Forgot from "./pages/Forgot";
import Join from "./pages/Join";
import Dashboard from "./pages/Dashboard";
import Attractions from "./pages/Attractions";
import Ledger from "./pages/Ledger";
import Settings from "./pages/Settings";
import Inspect from "./pages/Inspect";
import WindCheck from "./pages/WindCheck";
import AttractionEdit from "./pages/AttractionEdit";
import AttractionDetail from "./pages/AttractionDetail";
import Paywall from "./pages/Paywall";
import BillingSuccess from "./pages/BillingSuccess";
import LogDetail from "./pages/LogDetail";
import Reports from "./pages/Reports";
import Feedback from "./pages/Feedback";
import Admin from "./pages/Admin";
import MaintenanceNew from "./pages/MaintenanceNew";
import MaintenanceDetail from "./pages/MaintenanceDetail";
import IncidentNew from "./pages/IncidentNew";
import IncidentDetail from "./pages/IncidentDetail";
import TrainingNew from "./pages/TrainingNew";
import TrainingDetail from "./pages/TrainingDetail";
import Competency from "./pages/StaffTraining";
import Documents from "./pages/Documents";
import SharePack from "./pages/SharePack";
import DeliveryHandover from "./pages/DeliveryHandover";
import FoodSafety from "./pages/FoodSafety";
import AllergenMatrix from "./pages/AllergenMatrix";
import CleaningSchedule from "./pages/CleaningSchedule";
import CleaningChecklist from "./pages/CleaningChecklist";
import CateringDeliveryLog from "./pages/CateringDeliveryLog";
import DeliveryDetail from "./pages/DeliveryDetail";

function Spinner() {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: C.amber }}>
      <div className="display" style={{ fontSize: 22, fontWeight: 800 }}>SafeBook…</div>
    </div>
  );
}

function Shell({ children, tabs }) {
  return (
    <div className="app-shell" style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <div style={{ flex: 1 }}>{children}</div>
      {tabs && <BottomTabs />}
    </div>
  );
}

function Protected({ children, tabs }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc }} />;
  return <Shell tabs={tabs}>{children}</Shell>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (user) return <Navigate to={user.role === "super_admin" ? "/admin" : "/dashboard"} replace />;
  return <div className="app-shell">{children}</div>;
}

function Home() {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "super_admin" ? "/admin" : "/dashboard"} replace />;
}

function Bare({ children }) {
  return <div className="app-shell">{children}</div>;
}

// Adopts the signed-in user's saved profile language on login / profile refresh.
function LangSync() {
  const { user } = useAuth();
  const { lang, setLang } = useI18n();
  React.useEffect(() => {
    if (user?.language && user.language !== lang) setLang(user.language);
  }, [user?.language]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function SyncOnReconnect() {
  React.useEffect(() => {
    syncPending();
    const on = () => syncPending();
    window.addEventListener("online", on);
    return () => window.removeEventListener("online", on);
  }, []);
  return null;
}

export default function App() {
  return (
    <I18nProvider>
    <BrowserRouter>
      <AuthProvider>
        <SyncOnReconnect />
        <LangSync />
        <TermsGate />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/forgot-password" element={<Bare><Forgot /></Bare>} />
          <Route path="/join" element={<Bare><Join /></Bare>} />

          <Route path="/dashboard" element={<Protected tabs><Dashboard /></Protected>} />
          <Route path="/attractions" element={<Protected tabs><Attractions /></Protected>} />
          <Route path="/ledger" element={<Protected tabs><Ledger /></Protected>} />
          <Route path="/settings" element={<Protected tabs><Settings /></Protected>} />

          <Route path="/inspect/:id" element={<Protected><Inspect /></Protected>} />
          <Route path="/attraction/:id/food-safety" element={<Protected><FoodSafety /></Protected>} />
          <Route path="/attraction/:id/allergen-matrix" element={<Protected><AllergenMatrix /></Protected>} />
          <Route path="/attraction/:id/cleaning-schedule" element={<Protected><CleaningSchedule /></Protected>} />
          <Route path="/attraction/:id/cleaning" element={<Protected><CleaningChecklist /></Protected>} />
          <Route path="/attraction/:id/catering-delivery" element={<Protected><CateringDeliveryLog /></Protected>} />
          <Route path="/wind-check" element={<Protected><WindCheck /></Protected>} />
          <Route path="/attraction/new" element={<Protected><AttractionEdit /></Protected>} />
          <Route path="/attraction/:id" element={<Protected><AttractionDetail /></Protected>} />
          <Route path="/attraction/:id/edit" element={<Protected><AttractionEdit /></Protected>} />
          <Route path="/paywall" element={<Protected><Paywall /></Protected>} />
          <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
          <Route path="/log/:id" element={<Protected><LogDetail /></Protected>} />
          <Route path="/reports" element={<Protected><Reports /></Protected>} />
          <Route path="/feedback" element={<Protected><Feedback /></Protected>} />
          <Route path="/admin" element={<Protected><Admin /></Protected>} />
          <Route path="/maintenance/new" element={<Protected><MaintenanceNew /></Protected>} />
          <Route path="/maintenance/:id" element={<Protected><MaintenanceDetail /></Protected>} />
          <Route path="/incident/new/:aid" element={<Protected><IncidentNew /></Protected>} />
          <Route path="/incident/:id" element={<Protected><IncidentDetail /></Protected>} />
          <Route path="/training/new/:aid" element={<Protected><TrainingNew /></Protected>} />
          <Route path="/training/:id" element={<Protected><TrainingDetail /></Protected>} />
          <Route path="/attraction/:id/training" element={<Protected><Competency /></Protected>} />
          <Route path="/attraction/:id/delivery" element={<Protected><DeliveryHandover /></Protected>} />
          <Route path="/delivery/:id" element={<Protected><DeliveryDetail /></Protected>} />
          <Route path="/documents/:aid" element={<Protected><Documents /></Protected>} />
          <Route path="/share-pack" element={<Protected><SharePack /></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
    </I18nProvider>
  );
}
